import { observer } from "mobx-react";
import React, { useState } from "react";
import { translations } from "@/constants/translations";
import { useStore } from "@/store";
import { AppLink } from "@/routing/AppLink";
import { OrderCard } from "../_shared/OrderCard";
import { OrderCardSummaryItem } from "../_shared/OrderCard/OrderCardSummaryItem";
import { Divider, Text } from "@chakra-ui/react";
import { PromoCodePanel } from "../Details/UnitDetails/PromoCodePanel";
import { PromoCodeError } from "../Details/UnitDetails/PromoCodeError";

interface Props {
  isFinalCost: boolean;
}

export const OrderSummaryCard = observer(({ isFinalCost }: Props) => {
  const {
    activeLanguage,
    orderProducts,
    orderData,
    orderTotals,
    getShippingCostFormatted,
    isOntarioFlow,
    isIndigenousFlow,
  } = useStore();
  const language = translations.pages["order-summary"][activeLanguage];

  const [hasPromoCodeError, setHasPromoCodeError] = useState(false);

  const editLink = !isFinalCost ? (
    <AppLink
      to="/order/details"
      state={{
        jumpTo: "form",
      }}
    >
      {language.edit}
    </AppLink>
  ) : undefined;

  return (
    <OrderCard
      title={language.box_order_details}
      titleContent={editLink}
    >
      {orderProducts.map((product) => {
        if (isOntarioFlow && product.quantity === 0) return null;

        return (
          <React.Fragment key={product.name}>
            <OrderCardSummaryItem
              label={product.language.numberOfLabel}
              value={product.quantity}
              spaced
            />
            <OrderCardSummaryItem
              label={language.subtotal}
              value={product.totalFormatted}
              spaced
            />
          </React.Fragment>
        );
      })}

      {!isFinalCost && !isIndigenousFlow && (
        <>
          <Divider borderColor="gray.200" />
          <PromoCodePanel
            colorScheme="dark"
            showError={false}
            onApiError={setHasPromoCodeError}
          />
        </>
      )}

      {orderData.promotionalCode && (
        <>
          <Divider borderColor="gray.200" />
          {orderProducts.length > 1 && (
            <OrderCardSummaryItem
              label={isFinalCost ? "Order subtotal:" : "Order:"}
              value={orderTotals.formatted.subtotal}
              spaced
            />
          )}
          <OrderCardSummaryItem
            label="Discount:"
            value={orderTotals.formatted.discount}
            spaced
          />
        </>
      )}

      <Divider borderColor="gray.200" />

      {!isFinalCost && (
        <OrderCardSummaryItem
          label="Order subtotal:"
          value={orderTotals.formatted.subtotalWithDiscount}
          spaced
        />
      )}

      {isFinalCost && (
        <>
          <OrderCardSummaryItem
            label={language.shipping}
            value={getShippingCostFormatted()}
            spaced
          />
          {orderData.tax > 0 && (
            <OrderCardSummaryItem
              label={language.tax}
              value={orderTotals.formatted.tax}
              spaced
            />
          )}
          <OrderCardSummaryItem
            label={language.total}
            value={orderTotals.formatted.total}
            color="primary"
            fontWeight="bold"
            spaced
          />
        </>
      )}

      {orderData.promotionalCode && !isFinalCost && (
        <Text color="primary">Promotional code applied successfully</Text>
      )}

      {hasPromoCodeError && <PromoCodeError />}
    </OrderCard>
  );
});
